/**
 * Simple fiducial marker patterns for exam paper corner detection.
 *
 * Each marker is a 5×5 binary grid inside a 1-cell black border (7×7 total).
 * The 4 markers have distinct patterns so we can identify which corner is which.
 * Order: TL(0), TR(1), BR(2), BL(3) — matches PAPER_DST corner order.
 *
 * Print these at ~1.5cm × 1.5cm on each paper corner.
 */

/** 5×5 inner grid patterns. 1 = black, 0 = white. */
export const MARKER_PATTERNS: number[][] = [
  // ID 0 — top-left: L-shape
  [
    1, 1, 1, 0, 0,
    1, 0, 0, 0, 0,
    1, 0, 0, 0, 0,
    1, 0, 0, 0, 0,
    1, 0, 0, 0, 0,
  ],
  // ID 1 — top-right: reversed-L
  [
    0, 0, 1, 1, 1,
    0, 0, 0, 0, 1,
    0, 0, 0, 0, 1,
    0, 0, 0, 0, 1,
    0, 0, 0, 0, 1,
  ],
  // ID 2 — bottom-right: inverted reversed-L
  [
    0, 0, 0, 0, 1,
    0, 0, 0, 0, 1,
    0, 0, 0, 0, 1,
    0, 0, 0, 0, 1,
    0, 0, 1, 1, 1,
  ],
  // ID 3 — bottom-left: inverted L
  [
    1, 0, 0, 0, 0,
    1, 0, 0, 0, 0,
    1, 0, 0, 0, 0,
    1, 0, 0, 0, 0,
    1, 1, 1, 0, 0,
  ],
];

/**
 * Generate a data URL for a marker image (for printing).
 * Returns a PNG data URL rendered on a temporary canvas.
 */
export function generateMarkerDataURL(markerId: number, sizePx = 140): string {
  const pattern = MARKER_PATTERNS[markerId];
  if (!pattern) throw new Error(`Invalid marker ID: ${markerId}`);

  const canvas = document.createElement('canvas');
  canvas.width = sizePx;
  canvas.height = sizePx;
  const ctx = canvas.getContext('2d')!;

  const gridSize = 7; // 5×5 inner + 1-cell border on each side
  const cellPx = sizePx / gridSize;

  // Fill entire marker black (border)
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, sizePx, sizePx);

  // Fill inner white background
  ctx.fillStyle = '#fff';
  ctx.fillRect(cellPx, cellPx, cellPx * 5, cellPx * 5);

  // Draw inner pattern
  ctx.fillStyle = '#000';
  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 5; c++) {
      if (pattern[r * 5 + c] === 1) {
        ctx.fillRect((c + 1) * cellPx, (r + 1) * cellPx, cellPx, cellPx);
      }
    }
  }

  return canvas.toDataURL('image/png');
}
